import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { useInfiniteHits, useInstantSearchContext } from 'react-instantsearch';
import SectionBlock from '../ui/section-block';
import ProductsGrid from './group-products/products-grid';
import ProductsSlider from './group-products/slider';
import {
  DEFAULT_SECTION_STYLE,
  DynamicSectionComponents,
  TSectionStyles,
} from '@/types/dynamic-sections';
import { PRODUCTS_PER_PAGE } from '@/framework/client/variables';
import { buildHomeListContext } from '@/lib/gtm';

type Props = {
  uniqueKey: string;
  component?: string;
  title?: string;
  items_per_page?: number;
  styles?: TSectionStyles;
  id?: string;
  type?: string;
  order?: number;
  /** Render nothing until the first real search response (SSR shelf covers it). */
  hideUntilLive?: boolean;
  /** Called before paint once the first real search response is rendered. */
  onLiveReady?: () => void;
};

const MAP_COMPONENTS: { [key: string]: React.FunctionComponent<any> } = {
  [DynamicSectionComponents.PRODUCTS_GRID]: ProductsGrid,
  [DynamicSectionComponents.PRODUCTS_SLIDER]: ProductsSlider,
};

const EmptyComponent = ({ children }: { children: React.ReactNode }) =>
  children;

export const DynamicProductsWrapper = ({
  component = DynamicSectionComponents.PRODUCTS_GRID,
  uniqueKey,
  title,
  styles = DEFAULT_SECTION_STYLE,
  items_per_page,
  id,
  type,
  order,
  hideUntilLive = false,
  onLiveReady,
}: Props) => {
  const { isLastPage, showMore, items, results } = useInfiniteHits({
    showPrevious: true,
  });
  const { status } = useInstantSearchContext();
  const [isLoading, setIsLoading] = React.useState(true);
  const hasLiveResults =
    status === 'idle' &&
    Boolean(results) &&
    !(results as { __isArtificial?: boolean }).__isArtificial;

  useLayoutEffect(() => {
    if (hasLiveResults) onLiveReady?.();
  }, [hasLiveResults, onLiveReady]);

  // TODO: test loading if works apply to all components
  useEffect(() => {
    setIsLoading(results?.query === '' || status !== 'idle');
  }, [status, results]);

  const ConditionalSectionBlock = useMemo(
    () => (title ? SectionBlock : ({ children }: any) => children),
    [title],
  );

  const Component = useMemo(
    () => MAP_COMPONENTS?.[component] || EmptyComponent,
    [component],
  );

  const listContext = useMemo(
    () => buildHomeListContext({ id, type, title, order }),
    [id, type, title, order],
  );

  if (hideUntilLive && !hasLiveResults) return null;

  return (
    <ConditionalSectionBlock style={styles?.title} title={title}>
      <Component
        uniqueKey={uniqueKey}
        isLastPage={isLastPage}
        showMore={showMore}
        items={items}
        limit={items_per_page || PRODUCTS_PER_PAGE}
        products={items}
        isLoading={isLoading}
        listContext={listContext}
      />
    </ConditionalSectionBlock>
  );
};
